import { inject, Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { SsApiService } from './api/ss-api.service';
import { catchError, distinctUntilChanged, filter, first, map, switchMap, tap } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, from, Observable, of } from 'rxjs';
import { LocalstorageService } from './localstorage.service';
import { PlatformService } from './platform.service';
import { CmsApiService } from './api/cms-api.service';
import { UserService } from './user/user.service';
import { CookieService } from 'ngx-unificator/services';
import { FIRST_DEP } from './user/data/user-group.data';
import { WINDOW } from '@ng-web-apis/common';
import { LanguageService } from './language/language.service';
import { GroupsService } from './groups.service';
import { BrandConfigService } from './brand-config/brand-config.service';

export const LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT = 'LAST_DEP_DATA';
const DEFAULT_IP = '0.0.0.0';

interface UbidexRequest {
  ua: string; // ubidex will not work without this field
  ip: string; // ubidex will not work without this field
  s2s: any; // ubidex will not work without this field
  uid: string;
  cur: string;
  clickid: string;
  site: string;
  ln: string;
  affid: string;
  ts: any;
  event: string;
  auth: string;
  [key: string]: any;
}

enum UbidexEvent {
  REGISTER = 'regfinished',
  LOGIN = 'login',
  VISIT = 'visit',
  DEPOSIT = 'deposit',
  FTD = 'ftd'
}
enum UbidexAuth {
  REGISTER = '4rwvby',
  LOGIN = 'dn27hy',
  VISIT = 'dzy5b9',
  DEPOSIT = 'dg19aj',
  FTD = '9y3bmn'
}


@Injectable({
  providedIn: 'root',
})
export class UbidexService {
  private _window = inject<Window>(WINDOW);
  private _apiSs = inject(SsApiService);
  private _api = inject(CmsApiService);
  private _storage = inject(LocalstorageService);
  private _lang = inject(LanguageService);
  private _platform = inject(PlatformService);
  private _user = inject(UserService);
  private _groups = inject(GroupsService);
  private _cookie = inject(CookieService);
  private _brandConfig = inject(BrandConfigService);

  private _trackingDomain = 'https://pixel-us.mymetricpath.xyz';
  private _userIp$ = new BehaviorSubject<string>(DEFAULT_IP);

  initUbidex() {
    if (this._platform.isBrowser) {
      this._brandConfig.brandConfigResolved$.pipe(
        filter(Boolean),
        first(),
        tap(() => {
          this._initUserIp();
        })
      ).subscribe()
    }
  }
  sendDepositEvents(isSkipFTD = false): Observable<any> {
    const data = JSON.parse(this._storage.get(LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT) || '{}');
    return data.isFirst && !isSkipFTD ? this.onFTD(data) : this.onDeposit(data);
  }

  onRegister(): void {
    this.clearStorage();
    if (this._user.info?.id) {
      this._trackEvent(UbidexEvent.REGISTER, UbidexAuth.REGISTER).subscribe();
    }
  }

  onLogin(): void {
    this.clearStorage();
    if (this._user.info?.id) {
      this._trackEvent(UbidexEvent.LOGIN, UbidexAuth.LOGIN).subscribe();
    }
  }

  onVisit(): void {
    if (!this._platform.isBrowser) return;

    const sessionKey = this._user.info?.id ? 'visited' : 'visited-non-auth';
    if (this._window.sessionStorage.getItem(sessionKey)) return;

    this._trackEvent(UbidexEvent.VISIT, UbidexAuth.VISIT).subscribe(() => {
      this._window.sessionStorage.setItem(sessionKey, '1');
    });
  }

  onDeposit(depData: any): Observable<any> {
    if (!depData.amount || !depData.currency) return of(true);
    return this._trackEvent(UbidexEvent.DEPOSIT, UbidexAuth.DEPOSIT, {
      amount: String((Number(depData?.amount) ?? 0)),
      currency: depData?.currency
    });
  }

  onFTD(depData: any): Observable<any> {
    if (!depData.amount || !depData.currency || !depData.isFirst) return of(true);
    return this._trackEvent(UbidexEvent.FTD, UbidexAuth.FTD, {
      amount: String((Number(depData?.amount) ?? 0)),
      currency: depData?.currency,
    });
  }

  async setDepositDataUbidex(data: any): Promise<any> {
    return await firstValueFrom(this._user.getUserInfo().pipe(
      filter(user => !!user?.id),
      tap(() => {
        this._storage.set(LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT, JSON.stringify({
          amount: data.amount,
          currency: data.currency,
          playerId: data.playerId,
          isFirst: !this._groups.isExistGroup(FIRST_DEP)
        }));
      })
    ))
  }

  isStorageLength(dataType: string): boolean {
    return Boolean(this._storage.get(dataType) && this._storage.get(dataType).length);
  }

  getPlayerId(): number {
    const data = this._storage.get(LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT);
    return data ? +JSON.parse(data)?.playerId : 0;
  }

  clearStorage(): void {
    this._storage.clearItem(LOCAL_STORAGE_UBIDEX_KEY_DEPOSIT);
  }

  private _trackEvent(event: UbidexEvent, auth: UbidexAuth, additionalParams?: any): Observable<any> {
    return this._userIp$.pipe(
      first(),
      switchMap(ip => {
        const requestParams: UbidexRequest = {
          s2s: 1,
          ua: this._window.navigator.userAgent,
          ip,
          event,
          site: this._window.location.origin.includes('localhost') ? environment.app_host : this._window.location.origin,
          ts: Date.now(),
          affid: this._cookie.get('id_id') || '',
          ln: this._lang.current,
          clickid: null,
          cur: additionalParams?.currency || this._user.info?.currency || 'none',
          uid: this._user.info?.id || 'none',
          auth,
          ...additionalParams,
        };
        return this.sendPixel(requestParams);
      }),
      catchError(error => {
        this.logError({ event, auth, error });
        return of(error);
      }),
    );
  }

  private sendPixel(params: Record<string, any>): Observable<any> {
    const url = `${this._trackingDomain}/pixel?${new URLSearchParams(params)}`;
    return from(fetch(url, { mode: 'no-cors' }));
  }

  private logError(data): void {
    this._api.postUbidexLog(data).pipe(first()).subscribe();
  }
  private _initUserIp(): void {
    this._user.auth$
      .pipe(
        distinctUntilChanged(),
        switchMap((auth) => this._getIp$()),
        tap((data) => {
          const ip = data?.ip || DEFAULT_IP;
          this._userIp$.next(ip);
        }),
        tap(() => {
          // send on visit if no session storage date for auth / unauth user
          this.onVisit();
        })
      )
      .subscribe();
  }


  /**
   * Get ip for not auth user
   * @private
   */
  private _getIp$(): Observable<{ip: string}> {
    return this._apiSs.currentIp().pipe(
      map(data => ({ip: data?.ip || DEFAULT_IP})),
      catchError(() => of({ip: DEFAULT_IP }))
    )
  }
}
